import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  where
} from 'firebase/firestore';
import { db } from './firebase';
import { createPackageNotificationForUsers } from './notificationService';

export const PACKAGE_TYPES = {
  JOB_SEEKER: 'job_seeker',
  EMPLOYER: 'employer',
  FRANCHISE: 'franchise'
};

export const PACKAGE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive'
};

// Create a new package
export const createPackage = async (packageData, sendNotifications = true) => {
  try {
    const newPackage = {
      ...packageData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      status: packageData.status || PACKAGE_STATUS.ACTIVE
    };

    const docRef = await addDoc(collection(db, 'packages'), newPackage);
    const packageId = docRef.id;

    // Send notifications to relevant users if enabled
    if (sendNotifications) {
      try {
        // Determine which user types should receive notifications
        let targetUserTypes = [];
        if (packageData.type === PACKAGE_TYPES.JOB_SEEKER) {
          targetUserTypes = ['jobseeker'];
        } else if (packageData.type === PACKAGE_TYPES.EMPLOYER) {
          targetUserTypes = ['employer'];
        } else if (packageData.type === PACKAGE_TYPES.FRANCHISE) {
          targetUserTypes = ['franchise'];
        } else {
          // If type is not specified, notify both
          targetUserTypes = ['jobseeker', 'employer'];
        }

        const notificationResult = await createPackageNotificationForUsers(
          { ...newPackage, id: packageId },
          targetUserTypes
        );

        if (notificationResult.success) {
          console.log(`Package notifications sent to ${notificationResult.notificationsCreated} users`);
        } else {
          console.warn('Failed to send package notifications:', notificationResult.error);
        }
      } catch (notificationError) {
        console.warn('Error sending package notifications:', notificationError);
        // Don't fail the package creation if notifications fail
      }
    }

    return { success: true, id: packageId };
  } catch (error) {
    console.error('Error creating package:', error);
    return { success: false, error: error.message };
  }
};

// Get all packages
export const getAllPackages = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'packages'));

    const packages = [];
    querySnapshot.forEach((doc) => {
      packages.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort in memory by createdAt (newest first)
    packages.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, packages };
  } catch (error) {
    console.error('Error fetching packages:', error);
    return { success: false, error: error.message };
  }
};

// Get packages by type
export const getPackagesByType = async (type) => {
  try {
    const q = query(
      collection(db, 'packages'),
      where('type', '==', type),
      where('status', '==', PACKAGE_STATUS.ACTIVE)
    );
    const querySnapshot = await getDocs(q);

    const packages = [];
    querySnapshot.forEach((doc) => {
      packages.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort in memory by createdAt (newest first)
    packages.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, packages };
  } catch (error) {
    console.error('Error fetching packages by type:', error);
    return { success: false, error: error.message };
  }
};

// Update a package
export const updatePackage = async (packageId, updateData) => {
  try {
    const packageRef = doc(db, 'packages', packageId);
    await updateDoc(packageRef, {
      ...updateData,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating package:', error);
    return { success: false, error: error.message };
  }
};

// Delete a package
export const deletePackage = async (packageId) => {
  try {
    await deleteDoc(doc(db, 'packages', packageId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting package:', error);
    return { success: false, error: error.message };
  }
};

// Toggle package status
export const togglePackageStatus = async (packageId, currentStatus) => {
  try {
    const newStatus = currentStatus === PACKAGE_STATUS.ACTIVE
      ? PACKAGE_STATUS.INACTIVE
      : PACKAGE_STATUS.ACTIVE;

    const packageRef = doc(db, 'packages', packageId);
    await updateDoc(packageRef, {
      status: newStatus,
      updatedAt: Timestamp.now()
    });

    return { success: true, newStatus };
  } catch (error) {
    console.error('Error toggling package status:', error);
    return { success: false, error: error.message };
  }
};

// Default package templates
export const getDefaultPackageTemplate = (type) => {
  const baseTemplate = {
    name: '',
    description: '',
    price: 0,
    duration: 30, // days
    type: type,
    status: PACKAGE_STATUS.ACTIVE
  };

  if (type === PACKAGE_TYPES.JOB_SEEKER) {
    return {
      ...baseTemplate,
      maxApplications: 10
    };
  } else if (type === PACKAGE_TYPES.EMPLOYER) {
    return {
      ...baseTemplate,
      maxJobPostings: 5,
      candidateSearch: true,
      featuredJobs: 0,
      analyticsAccess: false
    };
  }

  return baseTemplate;
};

// Get marketing packages
export const getMarketingPackages = async () => {
  try {
    const q = query(
      collection(db, 'marketingPackages'),
      where('status', '==', PACKAGE_STATUS.ACTIVE)
    );
    const querySnapshot = await getDocs(q);

    const packages = [];
    querySnapshot.forEach((doc) => {
      packages.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort by price ascending
    packages.sort((a, b) => (a.price || 0) - (b.price || 0));

    return { success: true, packages };
  } catch (error) {
    console.error('Error fetching marketing packages:', error);
    return { success: false, error: error.message };
  }
};
